import { useQuery } from "@apollo/client";
import { OpenInNew } from "@mui/icons-material";
import { Alert, Box, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement, useEffect, useState } from "react";
import {
  ArrayParam,
  DateParam,
  NumberParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  DonationHistorySource,
  DonationPlatform,
  GetActBlueDonationHistoryImportsQuery,
  GetActBlueDonationHistoryImportsQueryVariables,
  SearchDonationHistoryQuery,
  SearchDonationHistoryQueryVariables,
  TrackedJobStatus,
  UsState,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import { InternalLink, LinkButton } from "~frontend/components/Link";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import Table from "~frontend/components/Table";
import { SEARCH_DONATION_HISTORY } from "~frontend/graphql/donationHistory";
import { GET_ACT_BLUE_DONATION_HISTORY_IMPORTS } from "~frontend/graphql/integration";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { formatDateTime } from "~frontend/util/dateFormat";
import { formatDollars } from "~frontend/util/formatDollars";
import { jobInProgress } from "~frontend/util/jobs";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import { plural } from "~frontend/util/plural";
import useDebounce from "~frontend/util/useDebounce";
import usePrevious from "~frontend/util/usePrevious";

import { DonationDetailsDialog } from "./DonationDetailsDialog";
import { DonationHistoryTableControls } from "./DonationHistoryTableControls";
import { DONATION_RECURRING_TYPES_LABELS } from "./recurringLabels";
import { DonationHistory } from "./types";

export function SubaccountDonationHistory(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  useBreadcrumbs([
    { label: "Donation History", path: subaccountLink("donation-history") },
  ]);

  const [startDate, setStartDate] = useQueryParam(
    "start_date",
    withDefault(DateParam, null)
  );
  const [endDate, setEndDate] = useQueryParam(
    "end_date",
    withDefault(DateParam, null)
  );
  const donationDateRange =
    startDate && endDate
      ? {
          startDate: DateTime.fromJSDate(startDate),
          endDate: DateTime.fromJSDate(endDate),
        }
      : null;
  const setDonationDateRange = (
    newValue: {
      startDate: DateTime;
      endDate: DateTime;
    } | null
  ) => {
    setStartDate(newValue?.startDate.toJSDate());
    setEndDate(newValue?.endDate.toJSDate());
    setPage(0);
  };

  const [maybeDonorStates, setDonorStates] = useQueryParam(
    "state",
    withDefault(ArrayParam, [])
  );
  const donorStates = maybeDonorStates.filter(ds => !!ds) as UsState[];

  const [maybeDonationSources, setDonationSources] = useQueryParam(
    "source",
    withDefault(ArrayParam, [])
  );
  const donationSources = maybeDonationSources.filter(
    ds => !!ds
  ) as DonationHistorySource[];

  const [maybeDonationForms, setDonationForms] = useQueryParam(
    "form",
    withDefault(ArrayParam, [])
  );
  const donationForms = maybeDonationForms.filter(df => !!df) as string[];

  const [search, setSearch] = useQueryParam(
    "search",
    withDefault(StringParam, "")
  );
  const debouncedSearch = useDebounce(search, 300);

  const [page, setPage] = useQueryParam("page", withDefault(NumberParam, 0));

  const [pageSize, setPageSize] = useQueryParam(
    "page_size",
    withDefault(NumberParam, 10)
  );

  const searchDonationHistoryQuery = useQuery<
    SearchDonationHistoryQuery,
    SearchDonationHistoryQueryVariables
  >(SEARCH_DONATION_HISTORY, {
    variables: {
      subaccountId: subaccount.id,
      filter: {
        donationDateRangeStart: donationDateRange?.startDate,
        donationDateRangeEnd: donationDateRange?.endDate.plus({ day: 1 }),
        donorStates,
        sources: donationSources,
        formNames: donationForms,
        search: debouncedSearch,
      },
      limit: pageSize,
      offset: page * pageSize,
    },
  });

  const getActBlueDonationHistoryImportsQuery = useQuery<
    GetActBlueDonationHistoryImportsQuery,
    GetActBlueDonationHistoryImportsQueryVariables
  >(GET_ACT_BLUE_DONATION_HISTORY_IMPORTS, {
    variables: { input: { subaccountId: subaccount.id } },
  });

  const actBlueImportInProgress =
    getActBlueDonationHistoryImportsQuery.data?.getActBlueDonationHistoryImports.some(
      importJob => jobInProgress(importJob)
    );
  const actBlueImportPreviouslyInProgress = usePrevious(
    actBlueImportInProgress
  );
  const [
    hasHadActBlueImportInCurrentPageView,
    setHasHadActBlueImportInCurrentPageView,
  ] = useState(false);

  useEffect(() => {
    if (actBlueImportInProgress) {
      setHasHadActBlueImportInCurrentPageView(true);
      getActBlueDonationHistoryImportsQuery.startPolling(1000);
    } else {
      getActBlueDonationHistoryImportsQuery.stopPolling();
    }

    return () => getActBlueDonationHistoryImportsQuery.stopPolling();
  }, [actBlueImportInProgress, getActBlueDonationHistoryImportsQuery]);

  useEffect(() => {
    if (actBlueImportPreviouslyInProgress && !actBlueImportInProgress) {
      void searchDonationHistoryQuery.refetch();
    }
  }, [
    actBlueImportPreviouslyInProgress,
    actBlueImportInProgress,
    searchDonationHistoryQuery,
  ]);

  const [donationForDetailsDialog, setDonationForDetailsDialog] =
    useState<DonationHistory | null>(null);

  const hasFilters =
    !!donationDateRange ||
    !!donorStates.length ||
    !!donationSources.length ||
    !!donationForms.length ||
    !!search.length;

  const clearFilters = () => {
    setDonationDateRange(null);
    setDonorStates([]);
    setDonationSources([]);
    setDonationForms([]);
    setSearch("");
  };

  return (
    <PageContainer>
      <PageHeader>Donation History</PageHeader>
      <Typography variant="h2" sx={{ marginBottom: "40px" }}>
        Your donation history will show any donations via the ActBlue webhook
        integration or donations you've imported manually. You can use this
        information to target your campaigns based on donation history (eg
        message contacts who've donated in the past year). See our{" "}
        <InternalLink to={subaccountLink("/integrations")}>
          integrations page
        </InternalLink>{" "}
        for more information on how to set up the ActBlue webhook.
      </Typography>
      <AsyncWrapper
        data={
          [
            searchDonationHistoryQuery.data,
            getActBlueDonationHistoryImportsQuery.data,
          ] as [
            SearchDonationHistoryQuery,
            GetActBlueDonationHistoryImportsQuery,
          ]
        }
        loading={[
          searchDonationHistoryQuery.loading,
          getActBlueDonationHistoryImportsQuery.loading,
        ]}
        errors={[
          searchDonationHistoryQuery.error,
          getActBlueDonationHistoryImportsQuery.error,
        ]}
      >
        {([
          { getDonationHistoryAggregateData, searchDonationHistory },
          { getActBlueDonationHistoryImports },
        ]) => (
          <>
            <DonationHistoryTableControls
              donationDateRange={donationDateRange}
              onChangeDonationDateRange={setDonationDateRange}
              donorStates={donorStates.filter(ds => !!ds) as string[]}
              onChangeDonorStates={setDonorStates}
              donationSources={donationSources.filter(ds => !!ds) as string[]}
              onChangeDonationSources={setDonationSources}
              donationForms={donationForms.filter(df => !!df) as string[]}
              onChangeDonationForms={setDonationForms}
              search={search}
              onChangeSearch={setSearch}
              importDonationHistoryDisabled={getActBlueDonationHistoryImports.some(
                importJob => jobInProgress(importJob)
              )}
            />
            <FlexBox
              justifyContent="space-between"
              alignItems="center"
              sx={{ padding: "0 40px 16px" }}
            >
              <Box>
                <FlexBox gap={2}>
                  <Typography variant="body3">
                    {plural(
                      getDonationHistoryAggregateData.count,
                      "Result",
                      "Results"
                    )}
                  </Typography>
                  {hasFilters ? (
                    <Typography variant="body3">
                      <LinkButton onClick={clearFilters}>
                        Clear Filters
                      </LinkButton>
                    </Typography>
                  ) : null}
                </FlexBox>
                <Typography variant="body2Bold">
                  Total Raised:{" "}
                  {formatDollars(
                    getDonationHistoryAggregateData.amountRaisedInCents / 100
                  )}
                </Typography>
              </Box>
              {hasHadActBlueImportInCurrentPageView &&
              getActBlueDonationHistoryImports.length ? (
                <ActBlueDonationHistoryImportAlert
                  importJob={getActBlueDonationHistoryImports[0]}
                />
              ) : null}
              <Box /> {/* Placeholder for export button */}
            </FlexBox>
            <Box sx={{ backgroundColor: palette.white, padding: "24px 40px" }}>
              <Table
                rows={searchDonationHistory}
                columns={[
                  {
                    key: "donation-amount",
                    title: "Donation Amount",
                    renderValue: row => {
                      const recurringText =
                        DONATION_RECURRING_TYPES_LABELS[row.recurringType];

                      return (
                        <>
                          <Typography>
                            {formatDollars(row.amountInCents / 100)}
                          </Typography>
                          {recurringText ? (
                            <Typography variant="body3">
                              {recurringText}
                            </Typography>
                          ) : null}
                        </>
                      );
                    },
                  },
                  {
                    key: "date",
                    title: "Date",
                    renderValue: row => formatDateTime(row.donatedAt),
                    align: "left",
                  },
                  {
                    key: "donor",
                    title: "Donor",
                    renderValue: row => (
                      <FlexBox direction="column">
                        {row.firstName ?? "(Unknown)"}{" "}
                        {row.lastName ?? "(Unknown)"}
                        {row.phoneNumber ? (
                          <Typography variant="body3">
                            {prettyPhoneNumber(row.phoneNumber)}
                          </Typography>
                        ) : null}
                        {row.city && row.state && row.zipCode ? (
                          <Typography variant="body3">
                            {row.city}, {row.state} {row.zipCode}
                          </Typography>
                        ) : null}
                      </FlexBox>
                    ),
                    align: "left",
                  },
                  {
                    key: "source",
                    title: "Source",
                    renderValue: row => (
                      <FlexBox direction="column">
                        {
                          { [DonationPlatform.ActBlue]: "ActBlue" }[
                            row.platform
                          ]
                        }
                        {row.formName ? (
                          <Typography variant="body3">
                            Form: {row.formName}
                          </Typography>
                        ) : null}
                        {row.campaign ? (
                          <Typography variant="body3">
                            Campaign:{" "}
                            <InternalLink
                              to={subaccountLink(
                                `/campaigns/${row.campaign.shortId}`
                              )}
                              target="_blank"
                            >
                              {row.campaign.name}
                            </InternalLink>
                          </Typography>
                        ) : null}
                      </FlexBox>
                    ),
                    align: "left",
                  },
                ]}
                rowActions={[
                  {
                    id: "open-details",
                    icon: <OpenInNew />,
                    onClick: row => setDonationForDetailsDialog(row),
                  },
                ]}
                totalCount={getDonationHistoryAggregateData.count}
                page={page}
                onChangePage={setPage}
                rowsPerPage={pageSize}
                onChangeRowsPerPage={setPageSize}
              />
            </Box>
          </>
        )}
      </AsyncWrapper>
      {donationForDetailsDialog ? (
        <DonationDetailsDialog
          donation={donationForDetailsDialog}
          onClose={() => setDonationForDetailsDialog(null)}
        />
      ) : null}
    </PageContainer>
  );
}

function formatDate(date: string): string {
  return DateTime.fromISO(date).toLocaleString(DateTime.DATE_SHORT);
}

function ActBlueDonationHistoryImportAlert({
  importJob,
}: {
  importJob: GetActBlueDonationHistoryImportsQuery["getActBlueDonationHistoryImports"][number];
}): ReactElement {
  switch (importJob.status) {
    case TrackedJobStatus.Created:
    case TrackedJobStatus.Queued:
    case TrackedJobStatus.Running:
      return (
        <Alert severity="info" color="tertiary" icon={false}>
          Importing donation history from {importJob.args.integration.name}...
        </Alert>
      );
    case TrackedJobStatus.Done:
      if (importJob.result?.nRecordsImported === 0) {
        return (
          <Alert severity="warning" icon={false}>
            <Typography variant="body2Bold">Import Finished:</Typography> 0 new
            donation records were found during the date range:{" "}
            {formatDate(importJob.args.startDate)} -{" "}
            {formatDate(importJob.args.endDate)}
          </Alert>
        );
      }
      return (
        <Alert severity="success" icon={false}>
          <Typography variant="body2Bold">Import Successful:</Typography>{" "}
          {importJob.result?.nRecordsImported.toLocaleString()} donation records
          imported from {importJob.args.integration.name}.
        </Alert>
      );
    case TrackedJobStatus.Aborted:
    case TrackedJobStatus.Canceled:
    case TrackedJobStatus.Failed:
    case TrackedJobStatus.Stalled:
      return <Alert severity="error">Import failed.</Alert>;
  }
}
